import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  type XAxisTickContentProps,
} from "recharts"
import { motion } from "framer-motion"
import {
  ClipboardCheck,
  Clock,
  CheckCircle,
  XCircle,
  Globe,
  FileBarChart,
} from "lucide-react"
import { FlagIcon } from "@/components/FlagIcon"
import { KPICard } from "./KPICard"
import { ChartCard } from "./ChartCard"
import { fetchPlansData } from "../api"
import type { DashboardFilters } from "../DashboardPage"

type PlansDimension = "market" | "channel"

interface PlansTabProps {
  filters: DashboardFilters
}

export function PlansTab({ filters }: PlansTabProps) {
  const [dimension, setDimension] = useState<PlansDimension>("market")

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "plans"],
    queryFn: fetchPlansData,
    staleTime: 2 * 60_000,
  })

  const overview = data?.overview
  const dimensionData = dimension === "market" ? data?.by_market ?? [] : data?.by_channel ?? []
  const importHistory = data?.import_history ?? []
  const totalImportRows = importHistory.reduce((s, i) => s + i.rows_processed, 0)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400" />
      </div>
    )
  }

  return (
    <div
      className="flex flex-col p-6 gap-5 overflow-hidden"
      style={{ height: "100%" }}
    >
      {/* KPI Row */}
      <div key={`kpis-${filters.animationKey}`} className="grid grid-cols-4 gap-4 flex-shrink-0">
        {[
          <KPICard
            key="total"
            icon={ClipboardCheck}
            label="Total Plans"
            value={(overview?.total ?? 0).toLocaleString()}
            subtext="All media plans"
          />,
          <KPICard
            key="pending"
            icon={Clock}
            label="Pending"
            value={(overview?.pending ?? 0).toLocaleString()}
            subtext="Awaiting approval"
          />,
          <KPICard
            key="approved"
            icon={CheckCircle}
            label="Approved"
            value={(overview?.approved ?? 0).toLocaleString()}
            subtext="Imported to tracker"
          />,
          <KPICard
            key="rejected"
            icon={XCircle}
            label="Rejected"
            value={(overview?.rejected ?? 0).toLocaleString()}
            subtext="Rejected plans"
          />,
        ].map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.06, ease: [0.4, 0, 0.2, 1] }}
          >
            {card}
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="flex-1 min-h-0 flex gap-5">
        {/* Plans by dimension */}
        <div className="flex-[3]">
          <ChartCard
            icon={Globe}
            title={dimension === "market" ? "Plans by Market" : "Plans by Channel"}
            subtitle="Distribution of media plans"
            controls={
              <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5">
                {([
                  { id: "market" as const, label: "Market" },
                  { id: "channel" as const, label: "Channel" },
                ]).map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setDimension(d.id)}
                    className={`h-7 px-2.5 text-xs font-medium rounded-md transition-colors ${
                      dimension === d.id
                        ? "bg-[#0014cc] text-white"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            }
          >
            {dimensionData.length > 0 ? (
              <ResponsiveContainer key={`plans-${dimension}-${filters.animationKey}`} width="100%" height="100%">
                <BarChart data={dimensionData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" strokeWidth={1} vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={dimension === "market" ? (props: XAxisTickContentProps) => {
                      const { x, y, payload } = props
                      return (
                        <g transform={`translate(${x},${Number(y) + 8})`}>
                          <foreignObject x={-14} y={0} width={28} height={20}>
                            <div className="flex flex-col items-center">
                              <FlagIcon countryCode={payload.value} className="w-3.5 h-auto rounded-[2px]" />
                            </div>
                          </foreignObject>
                        </g>
                      )
                    } : { fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    height={dimension === "market" ? 28 : 24}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null
                      const entry = payload[0].payload as { name: string; count: number }
                      const total = dimensionData.reduce((s, d) => s + d.count, 0)
                      const pct = total > 0 ? ((entry.count / total) * 100).toFixed(1) : "0.0"
                      return (
                        <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-600">
                          <div className="flex items-center gap-2 font-semibold mb-2">
                            {dimension === "market" && <FlagIcon countryCode={entry.name} className="w-4 h-auto rounded-[2px]" />}
                            {entry.name}
                          </div>
                          <div className="flex items-center justify-between gap-4 text-sm">
                            <span className="text-slate-300">Plans:</span>
                            <span className="font-mono font-medium">{entry.count}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4 text-sm">
                            <span className="text-slate-300">Share:</span>
                            <span className="font-mono font-medium">{pct}%</span>
                          </div>
                        </div>
                      )
                    }}
                  />
                  <Bar dataKey="count" fill="#93c5fd" radius={[4, 4, 0, 0]} maxBarSize={28} animationBegin={200} animationDuration={800} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                No plan data
              </div>
            )}
          </ChartCard>
        </div>

        {/* Import History */}
        <div className="flex-[2]">
          <ChartCard
            icon={FileBarChart}
            title="Import History"
            subtitle={`${importHistory.length} imports, ${totalImportRows.toLocaleString()} rows total`}
          >
            <div key={`imports-${filters.animationKey}`} className="h-full overflow-y-auto hover-scroll pr-2">
              {importHistory.length > 0 ? (
                importHistory.map((imp, i) => {
                  const dt = imp.date ? new Date(imp.date + "T00:00:00") : null
                  const dateLabel = dt ? dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Unknown"
                  return (
                    <motion.div
                      key={i}
                      className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-b-0"
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.08, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${imp.status === "completed" ? "bg-green-400" : imp.status === "failed" ? "bg-red-400" : "bg-amber-400"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{dateLabel}</span>
                          <span className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${
                            imp.status === "completed" ? "bg-green-50 text-green-700" : imp.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                          }`}>
                            {imp.status}
                          </span>
                        </div>
                        <div className="flex gap-4 text-xs text-slate-500">
                          <span>{imp.rows_processed.toLocaleString()} rows</span>
                          <span>{imp.campaigns_created.toLocaleString()} created</span>
                          {imp.errors > 0 && <span className="text-red-500">{imp.errors} errors</span>}
                        </div>
                      </div>
                    </motion.div>
                  )
                })
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  No imports yet
                </div>
              )}
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  )
}
